/**
 * barcode-lezer.js — barcodes lezen uit een camerabeeld, met dezelfde regels als de veldapp.
 *
 * De browser kan dit niet overal zelf: Chrome op een Mac en op Android heeft een ingebouwde
 * BarcodeDetector, Chrome op Windows (en dus een Windows-tablet), Firefox en Safari niet. Daar
 * gebruiken we ZXing (de lezer die ook achter veel scanner-apps zit), als WebAssembly. Die wordt
 * pas geladen als iemand de scanner opent, en het .wasm-bestand komt van onze eigen site -- niet
 * van een CDN.
 */

/** Dezelfde soorten codes als de veldapp (BarcodeScannerModal: qr, ean13, ean8, code128, code39, datamatrix). */
export const BARCODE_FORMATEN = ['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'data_matrix'];

let lezerBelofte = null;

/** Een BarcodeDetector-achtig object met `detect(bron)`. Ingebouwd als dat alle formaten kan, anders ZXing. */
export function maakBarcodeLezer() {
  if (!lezerBelofte) {
    lezerBelofte = (async () => {
      const Ingebouwd = globalThis.BarcodeDetector;
      if (Ingebouwd?.getSupportedFormats) {
        try {
          const kan = await Ingebouwd.getSupportedFormats();
          if (BARCODE_FORMATEN.every((f) => kan.includes(f))) return new Ingebouwd({ formats: BARCODE_FORMATEN });
        } catch { /* dan ZXing */ }
      }
      const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
        import('barcode-detector/ponyfill'),
        import('zxing-wasm/reader/zxing_reader.wasm?url'),
      ]);
      prepareZXingModule({
        overrides: { locateFile: (pad, prefix) => (pad.endsWith('.wasm') ? wasmUrl : prefix + pad) },
      });
      return new BarcodeDetector({ formats: BARCODE_FORMATEN });
    })().catch((e) => { lezerBelofte = null; throw e; });
  }
  return lezerBelofte;
}

/**
 * Accepteert een code pas als hij een tijdje stabiel gelezen wordt. Overgenomen van de veldapp
 * (BarcodeScannerModal.handleBarcodeScan): hoogstens ~15 lezingen per seconde tellen mee, en
 * dezelfde waarde moet 5 keer achter elkaar gelezen zijn, over minstens 250 ms. Eén wazig beeld
 * kan zo nooit een verkeerde code vastleggen.
 *
 * `lees(waarde, nu)` geeft de geaccepteerde code terug, of null zolang hij nog niet stabiel is.
 */
export function maakStabieleLezing({ minLezingen = 5, minDuurMs = 250, tussenMs = 67 } = {}) {
  let r = { waarde: '', aantal: 0, laatst: -Infinity, eerst: 0 };
  return {
    lees(waarde, nu = Date.now()) {
      if (!waarde) return null;
      if (nu - r.laatst < tussenMs) return null; // afknijpen: hoogstens ~15 lezingen per seconde
      r.laatst = nu;
      if (waarde === r.waarde) {
        r.aantal += 1;
      } else {
        r.waarde = waarde;
        r.aantal = 1;
        r.eerst = nu;
      }
      return r.aantal >= minLezingen && nu - r.eerst >= minDuurMs ? waarde : null;
    },
    reset() { r = { waarde: '', aantal: 0, laatst: -Infinity, eerst: 0 }; },
  };
}
